/**
 * Generic in-memory pub/sub factory.
 *
 * Creates a lightweight [subscribe, publish] pair backed by a Set of listeners.
 * Each event module keeps its own typed instance so subscribers only ever see
 * their event's payload shape. A throwing subscriber never breaks the others.
 */
export interface PubSub<T> {
	/** Subscribe to events. Returns an unsubscribe function. */
	subscribe: (listener: (payload: T) => void) => () => void
	/** Publish an event to all subscribers. */
	publish: (payload: T) => void
}

export function createPubSub<T>(): PubSub<T> {
	const listeners = new Set<(payload: T) => void>()

	return {
		subscribe(listener) {
			listeners.add(listener)
			return () => {
				listeners.delete(listener)
			}
		},
		publish(payload) {
			listeners.forEach(listener => {
				try {
					listener(payload)
				} catch {
					// A throwing subscriber must never break the other listeners.
				}
			})
		},
	}
}